import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, AlertTriangle, ChevronRight, RefreshCw, Truck } from 'lucide-react'
import { useI18n } from '../../../utils/i18n'
import { fetchCommerceOrder, fetchCommerceResourcePage, hasCommerceFulfillmentActivity, hasCommerceTrackingActivity, isCommerceLogisticsAuditEvent } from '../api'
import {
  ORDER_STATUS_LABELS,
  SECTION_CONFIG,
  TRACKING_STATUS_LABELS,
  DetailField,
  OrderAggregateDetails,
  StatusPill,
  errorMessage,
  exceptionRow,
  formatAmount,
  formatDate,
  orderRow,
  statusLabel,
  text,
  toneForTracking,
} from './CommerceLogistics.helpers'

import CommerceExceptions from './CommerceExceptions'
import CommerceLogisticsActions from './CommerceLogisticsActions'
import { createLogisticsOperations } from './CommerceLogistics.actions.js'

export default function CommerceLogistics({ section = 'tracking' }) {
  if (section === 'exceptions') return <CommerceExceptions />
  return <CommerceLogisticsPage key={section} section={section} />
}

function CommerceLogisticsPage({ section }) {
  const actionOperations = useRef(null)
  if (!actionOperations.current) actionOperations.current = createLogisticsOperations()
  const { lang } = useI18n()
  const config = SECTION_CONFIG[section] || SECTION_CONFIG.tracking
  const isExceptions = config.apiSection === 'exceptions'
  const [records, setRecords] = useState([])
  const [state, setState] = useState('loading')
  const [error, setError] = useState('')
  const [selected, setSelected] = useState(null)
  const [detailAggregate, setDetailAggregate] = useState(null)
  const [detailState, setDetailState] = useState('idle')
  const [detailError, setDetailError] = useState('')
  const [detailAttempt, setDetailAttempt] = useState(0)
  const [actionBusy, setActionBusy] = useState(false)
  const [cursors, setCursors] = useState([''])
  const [nextCursor, setNextCursor] = useState(null)
  const [listAttempt, setListAttempt] = useState(0)
  const cursor = cursors[cursors.length - 1]

  const load = useCallback((signal) => {
    setState('loading')
    setError('')
    return fetchCommerceResourcePage(config.apiSection, { signal, filters: { cursor, limit: 50 } })
      .then((page) => {
        if (signal?.aborted) return
        setRecords(page.records)
        setNextCursor(page.nextCursor)
        setSelected(null)
        setState('ready')
      })
      .catch((cause) => {
        if (signal?.aborted) return
        setError(errorMessage(cause, lang))
        setState('error')
      })
  }, [config.apiSection, lang, cursor])

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load, listAttempt])

  const visibleRecords = useMemo(() => {
    if (isExceptions) return records.filter(isCommerceLogisticsAuditEvent)
    if (section === 'fulfillment') return records.filter(hasCommerceFulfillmentActivity)
    return records.filter(hasCommerceTrackingActivity)
  }, [isExceptions, records, section])
  const rows = useMemo(
    () => (isExceptions ? visibleRecords.map(exceptionRow) : visibleRecords.map(orderRow)),
    [isExceptions, visibleRecords],
  )

  const selectedRow = selected ? rows.find((row) => row.key === selected) : null
  const selectedOrderId = selectedRow && !isExceptions ? text(selectedRow.order?.id) : ''

  useEffect(() => {
    if (!selectedOrderId) {
      setDetailAggregate(null)
      setDetailState('idle')
      setDetailError('')
      return undefined
    }

    const controller = new AbortController()
    setDetailAggregate(null)
    setDetailError('')
    setDetailState('loading')
    fetchCommerceOrder(selectedOrderId, { signal: controller.signal })
      .then((aggregate) => {
        if (controller.signal.aborted) return
        setDetailAggregate(aggregate)
        setDetailState('ready')
      })
      .catch((cause) => {
        if (controller.signal.aborted) return
        setDetailError(errorMessage(cause, lang))
        setDetailState('error')
      })
    return () => controller.abort()
  }, [selectedOrderId, detailAttempt, lang])

  function selectRow(row) {
    if (!actionBusy) setSelected(row.key)
  }

  async function refreshAfterAction() {
    // Refresh both sources without unmounting the action form or losing its notice.
    const [page, aggregate] = await Promise.all([
      fetchCommerceResourcePage(config.apiSection, { filters: { cursor, limit: 50 } }),
      fetchCommerceOrder(selectedOrderId),
    ])
    setRecords(page.records)
    setNextCursor(page.nextCursor)
    setDetailAggregate(aggregate)
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Truck size={21} className="text-amber-600" aria-hidden="true" />
            <h2 className="text-xl font-extrabold text-brand-textDark dark:text-white">{config.title[lang === 'zh' ? 0 : 1]}</h2>
          </div>
          <p className="mt-1 text-sm text-brand-text dark:text-slate-400">{config.description[lang === 'zh' ? 0 : 1]}</p>
        </div>
        <button
          type="button"
          onClick={() => setListAttempt((attempt) => attempt + 1)}
          disabled={state === 'loading' || actionBusy}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
        >
          <RefreshCw size={15} className={state === 'loading' ? 'animate-spin' : ''} aria-hidden="true" />
          {lang === 'zh' ? '刷新' : 'Refresh'}
        </button>
      </header>

      <nav className="flex flex-wrap items-center gap-3 text-sm" aria-label={lang === 'zh' ? '物流分页' : 'Logistics pagination'}>
        <button type="button" className="rounded border px-3 py-2 disabled:opacity-50" disabled={state === 'loading' || actionBusy || cursors.length <= 1} onClick={() => setCursors((history) => history.slice(0, -1))}>{lang === 'zh' ? '上一页' : 'Previous'}</button>
        <span>{lang === 'zh' ? `第 ${cursors.length} 页` : `Page ${cursors.length}`}</span>
        <button type="button" className="rounded border px-3 py-2 disabled:opacity-50" disabled={state !== 'ready' || actionBusy || !nextCursor} onClick={() => setCursors((history) => [...history, nextCursor])}>{lang === 'zh' ? '下一页' : 'Next'}</button>
        <span className="text-slate-500">{lang === 'zh' ? '仅显示本页匹配记录；无匹配时仍可翻页。' : 'Matching records on this page only; continue paging even if none match.'}</span>
      </nav>

      {state === 'loading' && <p role="status" className="border border-slate-200 py-12 text-center text-sm text-slate-500 dark:border-slate-800">{lang === 'zh' ? '正在加载真实物流数据…' : 'Loading live logistics data…'}</p>}

      {state === 'error' && (
        <div role="alert" className="flex gap-3 border-l-4 border-rose-500 bg-rose-50 px-4 py-3 text-sm text-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
          <AlertCircle size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-bold">{lang === 'zh' ? '无法加载物流数据' : 'Unable to load logistics data'}</p>
            <p className="mt-1 break-words">{error}</p>
            <p className="mt-2 text-xs opacity-80">{lang === 'zh' ? '请确认管理员会话有效，并检查 Commerce 服务是否可用。' : 'Check the administrator session and Commerce service availability.'}</p>
          </div>
        </div>
      )}

      {state === 'ready' && rows.length === 0 && (
        <div className="border border-slate-200 py-12 text-center text-sm text-slate-500 dark:border-slate-800">
          <p>{config.empty[lang === 'zh' ? 0 : 1]}</p>
          <p className="mt-2 text-xs">{lang === 'zh' ? '没有使用占位数据。' : 'No placeholder data is shown.'}</p>
        </div>
      )}

      {state === 'ready' && rows.length > 0 && (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
          <div className="overflow-x-auto border border-slate-200 dark:border-slate-800">
            {isExceptions ? (
              <table className="w-full min-w-[720px] border-collapse text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-3 font-bold">{lang === 'zh' ? '订单' : 'Order'}</th>
                    <th className="px-4 py-3 font-bold">{lang === 'zh' ? '事件' : 'Event'}</th>
                    <th className="px-4 py-3 font-bold">{lang === 'zh' ? '结果' : 'Result'}</th>
                    <th className="px-4 py-3 font-bold">{lang === 'zh' ? '时间' : 'Created'}</th>
                    <th className="px-4 py-3 font-bold" aria-label={lang === 'zh' ? '查看' : 'View'} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {rows.map((row) => (
                    <tr key={row.key} className={`cursor-pointer hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500 dark:hover:bg-slate-900/60 ${selected === row.key ? 'bg-amber-50/60 dark:bg-amber-950/20' : ''}`} tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); selectRow(row) } }} onClick={() => selectRow(row)}>
                      <td className="px-4 py-3 font-semibold text-slate-800 dark:text-slate-100">{row.orderId || '—'}</td>
                      <td className="max-w-[230px] truncate px-4 py-3 text-slate-700 dark:text-slate-200" title={row.message || row.action}>{row.message || row.action || '—'}</td>
                      <td className="px-4 py-3"><StatusPill value={row.result} labels={{ failure: ['失败', 'Failure'], error: ['错误', 'Error'], success: ['成功', 'Success'], recorded: ['已记录', 'Recorded'] }} tone={row.result === 'failure' || row.result === 'error' ? 'danger' : 'neutral'} lang={lang} /></td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600 dark:text-slate-300">{formatDate(row.createdAt, lang)}</td>
                      <td className="px-4 py-3 text-right"><ChevronRight size={17} className="text-slate-400" aria-hidden="true" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full min-w-[820px] border-collapse text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-3 font-bold">{lang === 'zh' ? '订单' : 'Order'}</th>
                    <th className="px-4 py-3 font-bold">{lang === 'zh' ? '客户' : 'Customer'}</th>
                    <th className="px-4 py-3 font-bold">{lang === 'zh' ? '履约状态' : 'Fulfillment'}</th>
                    <th className="px-4 py-3 font-bold">{lang === 'zh' ? '追踪号' : 'Tracking number'}</th>
                    <th className="px-4 py-3 font-bold">{lang === 'zh' ? '物流状态' : 'Tracking status'}</th>
                    <th className="px-4 py-3 font-bold">{lang === 'zh' ? '更新时间' : 'Updated'}</th>
                    <th className="px-4 py-3 font-bold" aria-label={lang === 'zh' ? '查看' : 'View'} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {rows.map((row) => (
                    <tr key={row.key} className={`cursor-pointer hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500 dark:hover:bg-slate-900/60 ${selected === row.key ? 'bg-amber-50/60 dark:bg-amber-950/20' : ''}`} tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); selectRow(row) } }} onClick={() => selectRow(row)}>
                      <td className="px-4 py-3 font-semibold text-slate-800 dark:text-slate-100">{row.orderId || '—'}</td>
                      <td className="max-w-[190px] truncate px-4 py-3 text-slate-700 dark:text-slate-200" title={row.customer}>{row.customer || '—'}</td>
                      <td className="px-4 py-3"><StatusPill value={row.fulfillmentStatus || row.orderStatus} labels={ORDER_STATUS_LABELS} lang={lang} /></td>
                      <td className="max-w-[190px] truncate px-4 py-3 font-mono text-xs text-slate-700 dark:text-slate-200" title={row.trackingNumber}>{row.trackingNumber || '—'}</td>
                      <td className="px-4 py-3"><StatusPill value={row.trackingStatus} labels={TRACKING_STATUS_LABELS} tone={toneForTracking(row.trackingStatus, row.exception)} lang={lang} /></td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600 dark:text-slate-300">{formatDate(row.updatedAt, lang)}</td>
                      <td className="px-4 py-3 text-right"><ChevronRight size={17} className="text-slate-400" aria-hidden="true" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <aside className="space-y-4">
            {!selectedRow && (
              <div className="border border-dashed border-slate-300 px-5 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                {lang === 'zh' ? '选择一条记录查看详情和物流操作。' : 'Select a record to view details and logistics actions.'}
              </div>
            )}

            {selectedRow && isExceptions && (
              <div className="space-y-4 border border-slate-200 p-5 dark:border-slate-800">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={19} className="mt-0.5 shrink-0 text-rose-600" aria-hidden="true" />
                  <div>
                    <h3 className="font-bold text-slate-900 dark:text-white">{lang === 'zh' ? '异常事件详情' : 'Exception event details'}</h3>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{lang === 'zh' ? '以下是 Commerce 审计接口的记录，不代表每条都是物流异常。' : 'These are Commerce audit records; not all of them necessarily represent logistics exceptions.'}</p>
                  </div>
                </div>
                <dl className="grid gap-4 sm:grid-cols-2">
                  <DetailField label={lang === 'zh' ? '订单' : 'Order'} value={selectedRow.orderId} />
                  <DetailField label={lang === 'zh' ? '事件' : 'Event'} value={selectedRow.action} />
                  <DetailField label={lang === 'zh' ? '结果' : 'Result'} value={selectedRow.result} />
                  <DetailField label={lang === 'zh' ? '操作者' : 'Actor'} value={selectedRow.actor} />
                  <DetailField label={lang === 'zh' ? '时间' : 'Created'} value={formatDate(selectedRow.createdAt, lang)} />
                  <DetailField label={lang === 'zh' ? '说明' : 'Message'} value={selectedRow.message} />
                </dl>
              </div>
            )}

            {selectedRow && !isExceptions && (
              <>
                {detailState === 'loading' && <p role="status" className="border border-slate-200 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-800">{lang === 'zh' ? '正在读取订单完整物流详情…' : 'Loading full order logistics details…'}</p>}
                {detailState === 'error' && <div role="alert" className="space-y-3 border-l-4 border-rose-500 bg-rose-50 px-4 py-3 text-sm text-rose-800 dark:bg-rose-950/30 dark:text-rose-200"><p className="font-bold">{lang === 'zh' ? '无法读取订单详情' : 'Unable to load order details'}</p><p className="break-words">{detailError}</p><button type="button" onClick={() => setDetailAttempt((attempt) => attempt + 1)} className="font-bold underline">{lang === 'zh' ? '重试' : 'Retry'}</button></div>}
                {detailState === 'ready' && detailAggregate?.order?.id === selectedOrderId && (
                  <OrderAggregateDetails row={selectedRow} aggregate={detailAggregate} lang={lang} />
                )}

                {detailState === 'ready' && detailAggregate?.order?.id === selectedOrderId && (
                  <CommerceLogisticsActions key={selectedOrderId} aggregate={detailAggregate} lang={lang} operations={actionOperations.current} onSuccess={refreshAfterAction} onBusyChange={setActionBusy} />
                )}
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  )
}
